import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import Hls from "hls.js";
import { createPageUrl } from "@/utils";
import { useLanguage } from "@/components/ui/LanguageContext";
import { motion, AnimatePresence } from "framer-motion";
import {
  Play,
  Pause,
  SkipBack,
  SkipForward,
  Volume2,
  VolumeX,
  Settings,
  Subtitles,
  Maximize,
  Minimize,
  PictureInPicture2,
  Download,
  ChevronLeft,
  ChevronRight,
  Clock,
  FileText,
  Check,
  Share2,
  ExternalLink,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { getVideoDetail, getVideoShareUrl, postVideoView, postVideoViewKeepalive } from "@/api/content";
import { useRequireAuthRedirect } from "@/hooks/useRequireAuthRedirect";
import { useToast } from "@/components/ui/use-toast";
import { TAG_FILTER_DICTIONARY, getDictionaryLabel } from "@/config/catalogFilters";
import {
  DEFAULT_VOLUME,
  loadStoredVolume,
  saveStoredVolume,
} from "@/lib/playerVolumeStorage";

const CONTROLS_HIDE_DELAY_MS = 3000;
const VIEW_PROGRESS_REPORT_INTERVAL_MS = 15000;

function sanitizeDownloadFilename(name) {
  const s = String(name || "download").replace(/[/\\?%*:|"<>]/g, "_").trim();
  return s.slice(0, 200) || "download";
}

function suggestedMaterialFilename(material) {
  const fromName = (material?.name || "").trim();
  if (fromName && /\.[a-z0-9]{2,8}$/i.test(fromName)) {
    return sanitizeDownloadFilename(fromName);
  }
  try {
    const u = new URL(material.url);
    const seg = u.pathname.split("/").filter(Boolean).pop();
    if (seg) {
      const decoded = decodeURIComponent(seg);
      if (decoded) return sanitizeDownloadFilename(decoded);
    }
  } catch {
    /* ignore */
  }
  return sanitizeDownloadFilename(fromName || "download");
}

async function triggerMaterialDownload(material) {
  const url = material.url;
  const filename = suggestedMaterialFilename(material);
  try {
    const res = await fetch(url, { mode: "cors", credentials: "omit" });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = filename;
    a.rel = "noopener noreferrer";
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
  } catch {
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    document.body.appendChild(a);
    a.click();
    a.remove();
  }
}

function normalizeReturnTo(value) {
  return typeof value === "string" && value.startsWith("/") ? value : createPageUrl("Catalog");
}

function buildVideoTarget(video, returnTo) {
  const identifier = video?.id ?? video?.slug;
  const target = identifier ? createPageUrl(`VideoPlayer?id=${identifier}`) : createPageUrl("Catalog");
  if (!returnTo) return target;

  const separator = target.includes("?") ? "&" : "?";
  return `${target}${separator}returnTo=${encodeURIComponent(returnTo)}`;
}

export default function VideoPlayerPage() {
  const { t, language } = useLanguage();
  const { toast } = useToast();
  const { canRenderProtectedContent, isLoadingAuth } = useRequireAuthRedirect();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const videoId = searchParams.get("id");
  const returnTo = normalizeReturnTo(location.state?.returnTo || searchParams.get("returnTo"));
  const returnState = { returnTo };

  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const hideControlsTimerRef = useRef(null);
  const viewCountSentRef = useRef(false);
  const lastProgressReportAtRef = useRef(0);
  const restoredProgressForVideoRef = useRef(null);
  const hasPlaybackStartedRef = useRef(false);
  const suppressNextPauseFlushRef = useRef(false);

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  // 保存済みの音量設定があれば初期値に使う（動画ごとではなく再生ページ共通）
  const [volume, setVolume] = useState(() => loadStoredVolume()?.volume ?? DEFAULT_VOLUME);
  const [isMuted, setIsMuted] = useState(() => loadStoredVolume()?.isMuted ?? false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [subtitle, setSubtitle] = useState("off");
  const [autoPlay, setAutoPlay] = useState(true);
  const [showControls, setShowControls] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [playbackError, setPlaybackError] = useState(null);
  const [isMarkingWatched, setIsMarkingWatched] = useState(false);
  const [isMarkedWatched, setIsMarkedWatched] = useState(false);

  const {
    data: video,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["video-detail", videoId],
    queryFn: async () => getVideoDetail(videoId),
    enabled: canRenderProtectedContent && !!videoId,
  });

  const prevVideo = video?.prevVideo ?? null;
  const nextVideo = video?.nextVideo ?? null;
  const playbackSources =
    Array.isArray(video?.playback?.sources) && video.playback.sources.length > 0
      ? video.playback.sources
      : null;
  const videoSrc = video?.playback?.url || video?.video_url || null;
  const playbackType =
    video?.playback?.type || (videoSrc?.includes('.m3u8') ? 'hls' : 'mp4');
  const canPlayMedia =
    playbackType === 'hls'
      ? !!videoSrc
      : !!(playbackSources?.length || videoSrc);

  const clearHideControlsTimer = () => {
    if (hideControlsTimerRef.current) {
      clearTimeout(hideControlsTimerRef.current);
      hideControlsTimerRef.current = null;
    }
  };

  const bumpControlsVisibility = useCallback(() => {
    setShowControls(true);
    clearHideControlsTimer();
    if (isPlaying) {
      hideControlsTimerRef.current = setTimeout(() => {
        setShowControls(false);
        hideControlsTimerRef.current = null;
      }, CONTROLS_HIDE_DELAY_MS);
    }
  }, [isPlaying]);

  useEffect(() => {
    return () => clearHideControlsTimer();
  }, []);

  useEffect(() => {
    if (!isPlaying) {
      clearHideControlsTimer();
      setShowControls(true);
      return;
    }
    clearHideControlsTimer();
    hideControlsTimerRef.current = setTimeout(() => {
      setShowControls(false);
      hideControlsTimerRef.current = null;
    }, CONTROLS_HIDE_DELAY_MS);
    return () => clearHideControlsTimer();
  }, [isPlaying]);

  useEffect(() => {
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    setPlaybackError(null);
    setShowControls(true);
    clearHideControlsTimer();
    restoredProgressForVideoRef.current = null;
    hasPlaybackStartedRef.current = false;

    const el = videoRef.current;
    if (!el) return;

    suppressNextPauseFlushRef.current = !el.paused;
    el.pause();
    el.currentTime = 0;
    el.playbackRate = playbackSpeed;
    el.volume = isMuted ? 0 : volume;
    // 再生位置は動画 ID が変わったときだけリセット（倍速・音量ではリセットしない）
    // eslint-disable-next-line react-hooks/exhaustive-deps -- playbackSpeed / volume / isMuted は直後の effect で反映
  }, [video?.id]);

  useEffect(() => {
    viewCountSentRef.current = false;
    lastProgressReportAtRef.current = 0;
    setIsMarkedWatched(Boolean(video?.user_progress?.is_completed));
  }, [video?.id]);

  const restoreSavedProgress = useCallback(() => {
    const el = videoRef.current;
    if (!el || !video?.id || restoredProgressForVideoRef.current === video.id) return;
    if (video.user_progress?.is_completed) return;

    const savedSeconds = Math.floor(Number(video.user_progress?.progress_seconds) || 0);
    if (savedSeconds <= 0) return;

    const mediaDuration = Number.isFinite(el.duration) && el.duration > 0
      ? el.duration
      : Number(video.duration_seconds || 0);
    const shouldResume = !mediaDuration || savedSeconds < Math.max(0, mediaDuration - 5);
    if (!shouldResume) return;

    const next = mediaDuration
      ? Math.min(savedSeconds, Math.max(0, mediaDuration - 1))
      : savedSeconds;
    el.currentTime = next;
    setCurrentTime(next);
    restoredProgressForVideoRef.current = video.id;
  }, [video?.id, video?.duration_seconds, video?.user_progress]);

  useEffect(() => {
    const el = videoRef.current;
    if (!el || el.readyState < 1) return;
    restoreSavedProgress();
  }, [restoreSavedProgress]);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    el.playbackRate = playbackSpeed;
  }, [playbackSpeed, video?.id]);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    el.volume = isMuted ? 0 : volume;
  }, [volume, isMuted, video?.id]);

  useEffect(() => {
    saveStoredVolume({ volume, isMuted });
  }, [volume, isMuted]);

  useEffect(() => {
    const el = videoRef.current;
    if (!el || !canPlayMedia) return undefined;

    let hls;

    if (playbackType === 'hls') {
      if (el.canPlayType('application/vnd.apple.mpegurl')) {
        el.src = videoSrc;
      } else if (Hls.isSupported()) {
        hls = new Hls();
        hls.loadSource(videoSrc);
        hls.attachMedia(el);
        hls.on(Hls.Events.ERROR, (_event, data) => {
          if (data?.fatal) {
            setPlaybackError('Failed to load streaming video');
          }
        });
      } else {
        setPlaybackError('This browser does not support HLS playback');
      }
    } else if (playbackSources?.length) {
      el.removeAttribute('src');
      el.load();
    } else if (videoSrc) {
      el.src = videoSrc;
    }

    return () => {
      if (hls) {
        hls.destroy();
      }
    };
  }, [videoSrc, playbackType, playbackSources, canPlayMedia]);

  const flushWatchProgress = useCallback(
    async ({ recordView = false, useKeepalive = false, forceCompleted = false, progressSeconds } = {}) => {
      if (!videoId) return;
      const el = videoRef.current;
      const resolvedSeconds = Number.isFinite(progressSeconds)
        ? progressSeconds
        : Number.isFinite(el?.currentTime)
          ? el.currentTime
          : 0;
      const seconds = Math.max(0, Math.floor(resolvedSeconds));
      const isImplicitProgressSave = !recordView && !forceCompleted && !Number.isFinite(progressSeconds);
      const savedSeconds = Math.floor(Number(video?.user_progress?.progress_seconds) || 0);
      if (isImplicitProgressSave && !hasPlaybackStartedRef.current) return;
      if (isImplicitProgressSave && seconds === 0 && savedSeconds > 0) return;

      try {
        if (useKeepalive) {
          postVideoViewKeepalive(videoId, { progress_seconds: seconds, recordView, forceCompleted });
        } else {
          await postVideoView(videoId, { progress_seconds: seconds, recordView, forceCompleted });
        }
      } catch (e) {
        console.warn("postVideoView failed", e);
      }
    },
    [videoId, video?.user_progress?.progress_seconds]
  );

  useEffect(() => {
    const onHidden = () => {
      if (document.visibilityState !== "hidden") return;
      void flushWatchProgress({ recordView: false, useKeepalive: true });
    };
    document.addEventListener("visibilitychange", onHidden);
    return () => document.removeEventListener("visibilitychange", onHidden);
  }, [flushWatchProgress]);

  useEffect(() => {
    const onUnload = () => {
      flushWatchProgress({ recordView: false, useKeepalive: true });
    };
    window.addEventListener("pagehide", onUnload);
    return () => window.removeEventListener("pagehide", onUnload);
  }, [flushWatchProgress]);

  const togglePlay = async () => {
    const el = videoRef.current;
    if (!el) return;

    if (el.paused) {
      try {
        await el.play();
        hasPlaybackStartedRef.current = true;
        setIsPlaying(true);
        if (!viewCountSentRef.current && videoId) {
          viewCountSentRef.current = true;
          void flushWatchProgress({ recordView: true });
        }
      } catch (e) {
        console.error("play() failed:", e);
      }
    } else {
      el.pause();
      setIsPlaying(false);
    }
  };

  const handleTimeUpdate = () => {
    const el = videoRef.current;
    if (el) setCurrentTime(el.currentTime);

    if (!el || el.paused || !videoId) return;
    const now = Date.now();
    if (now - lastProgressReportAtRef.current < VIEW_PROGRESS_REPORT_INTERVAL_MS) return;
    lastProgressReportAtRef.current = now;
    void flushWatchProgress({ recordView: false });
  };

  const syncDurationFromVideo = () => {
    const el = videoRef.current;
    if (!el) return;
    const d = el.duration;
    if (Number.isFinite(d) && d > 0) setDuration(d);
  };

  const handleLoadedMetadata = () => {
    syncDurationFromVideo();
    restoreSavedProgress();
  };

  const handlePlay = () => {
    hasPlaybackStartedRef.current = true;
    setIsPlaying(true);
    if (!viewCountSentRef.current && videoId) {
      viewCountSentRef.current = true;
      void flushWatchProgress({ recordView: true });
    }
  };

  const handlePause = () => {
    setIsPlaying(false);
    if (suppressNextPauseFlushRef.current) {
      suppressNextPauseFlushRef.current = false;
      return;
    }
    void flushWatchProgress({ recordView: false });
  };

  const handleVideoEnd = () => {
    setIsPlaying(false);
    void flushWatchProgress({ recordView: false });
    if (autoPlay && nextVideo) {
      window.location.href = buildVideoTarget(nextVideo);
    }
  };

  const handleShare = async () => {
    const shareUrl = getVideoShareUrl(video?.id ?? videoId);
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast({ description: "共有用リンクをコピーしました" });
    } catch {
      toast({ description: shareUrl });
    }
  };

  const handleMarkAsWatched = async () => {
    if (!videoId || isMarkingWatched || isMarkedWatched) return;
    setIsMarkingWatched(true);
    try {
      const sourceDuration = Number(video?.duration_seconds || duration || 0);
      const fallbackSeconds = Math.max(1, Math.floor(sourceDuration));
      await flushWatchProgress({
        recordView: false,
        forceCompleted: true,
        progressSeconds: fallbackSeconds,
      });
      setIsMarkedWatched(true);
    } finally {
      setIsMarkingWatched(false);
    }
  };

  const seek = (seconds) => {
    if (!videoRef.current) return;
    const next = Math.max(
      0,
      Math.min(videoRef.current.currentTime + seconds, duration || 0)
    );
    videoRef.current.currentTime = next;
    setCurrentTime(next);
  };

  const handleSeek = (value) => {
    bumpControlsVisibility();
    if (!videoRef.current) return;
    const next = value[0];
    videoRef.current.currentTime = next;
    setCurrentTime(next);
  };

  const handleVolumeChange = (value) => {
    bumpControlsVisibility();
    const vol = value[0];
    setVolume(vol);
    const muted = vol === 0;
    setIsMuted(muted);
    if (videoRef.current) videoRef.current.volume = vol;
  };

  const toggleMute = () => {
    bumpControlsVisibility();
    if (isMuted) {
      // ミュート解除時に音量 0 のままにならないようデフォルト音量へ戻す
      const next = volume > 0 ? volume : DEFAULT_VOLUME;
      setVolume(next);
      setIsMuted(false);
      if (videoRef.current) videoRef.current.volume = next;
    } else {
      setIsMuted(true);
      if (videoRef.current) videoRef.current.volume = 0;
    }
  };

  const changePlaybackSpeed = (speed) => {
    bumpControlsVisibility();
    setPlaybackSpeed(speed);
    if (videoRef.current) videoRef.current.playbackRate = speed;
  };

  const toggleFullscreen = async () => {
    const container = containerRef.current;
    const videoEl = videoRef.current;
    if (!container) return;

    const isFs =
      document.fullscreenElement ||
      document.webkitFullscreenElement ||
      videoEl?.webkitDisplayingFullscreen;

    try {
      if (!isFs) {
        if (container.requestFullscreen) {
          await container.requestFullscreen();
        } else if (container.webkitRequestFullscreen) {
          await container.webkitRequestFullscreen();
        } else if (videoEl?.webkitEnterFullscreen) {
          videoEl.webkitEnterFullscreen();
        }
        setIsFullscreen(true);
        try {
          await screen.orientation?.lock?.("landscape");
        } catch {
          /* iOS / 非対応ブラウザでは無視 */
        }
      } else {
        if (document.exitFullscreen) {
          await document.exitFullscreen();
        } else if (document.webkitExitFullscreen) {
          await document.webkitExitFullscreen();
        } else if (videoEl?.webkitExitFullscreen) {
          videoEl.webkitExitFullscreen();
        }
        setIsFullscreen(false);
        try {
          screen.orientation?.unlock?.();
        } catch {
          /* ignore */
        }
      }
    } catch (e) {
      console.warn("fullscreen failed", e);
    }
  };

  useEffect(() => {
    const syncFullscreenState = () => {
      const active =
        !!document.fullscreenElement ||
        !!document.webkitFullscreenElement ||
        !!videoRef.current?.webkitDisplayingFullscreen;
      setIsFullscreen(active);
      if (!active) {
        try {
          screen.orientation?.unlock?.();
        } catch {
          /* ignore */
        }
      }
    };

    document.addEventListener("fullscreenchange", syncFullscreenState);
    document.addEventListener("webkitfullscreenchange", syncFullscreenState);
    const videoEl = videoRef.current;
    videoEl?.addEventListener?.("webkitbeginfullscreen", syncFullscreenState);
    videoEl?.addEventListener?.("webkitendfullscreen", syncFullscreenState);

    return () => {
      document.removeEventListener("fullscreenchange", syncFullscreenState);
      document.removeEventListener("webkitfullscreenchange", syncFullscreenState);
      videoEl?.removeEventListener?.("webkitbeginfullscreen", syncFullscreenState);
      videoEl?.removeEventListener?.("webkitendfullscreen", syncFullscreenState);
    };
  }, []);

  const togglePiP = async () => {
    if (document.pictureInPictureElement) {
      await document.exitPictureInPicture();
    } else if (videoRef.current) {
      await videoRef.current.requestPictureInPicture();
    }
  };

  const formatTime = (time) => {
    if (!Number.isFinite(time) || time < 0) return "0:00";
    const total = Math.floor(time);
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    if (h > 0) {
      return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
    }
    return `${m}:${String(s).padStart(2, "0")}`;
  };

  const playbackSpeeds = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];
  const handleMaterialClick = useCallback((e, material) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    e.preventDefault();
    void triggerMaterialDownload(material);
  }, []);

  const subtitleLanguages = [
    { code: "off", label: t("subtitleOff") },
    { code: "ja", label: "日本語" },
    { code: "en", label: "English" },
    { code: "es", label: "Español" },
  ];
  const videoTagSlugs = (Array.isArray(video?.tags) ? video.tags : [])
    .map((entry) => (typeof entry === "string" ? entry : entry?.slug))
    .filter(Boolean);

  if (isLoadingAuth || !canRenderProtectedContent) {
    return null;
  }

  const pageBgClass =
    "bg-gradient-to-b from-[#faf6f1] via-[#f5ede3] to-[#efe5d8] text-[#2d1810]";
  const pageShellClass = `${pageBgClass} min-h-screen flex items-center justify-center pb-0`;

  if (isLoading) {
    return (
      <div className={`${pageShellClass} flex items-center justify-center`}>
        <p className="text-[#6b5540]">Loading video...</p>
      </div>
    );
  }

  if (!video) {
    return (
      <div className={`${pageShellClass} flex items-center justify-center`}>
        <p className="text-[#6b5540]">{error ? "Failed to load video" : "Video not found"}</p>
      </div>
    );
  }

  if (!canPlayMedia) {
    return (
      <div className={`${pageShellClass} flex items-center justify-center`}>
        <p className="text-[#6b5540]">Video source is unavailable</p>
      </div>
    );
  }

  return (
    <div className={`${pageBgClass} pb-1`}>
      {/* ✅ 横幅を抑える（デカすぎ問題の解消） */}
      <div className="max-w-5xl mx-auto px-4 md:px-8 pt-4">
        <div
          ref={containerRef}
          className="relative w-full aspect-video bg-zinc-950 group rounded-2xl overflow-hidden shadow-lg ring-1 ring-[#d4c4b0]/60 text-white"
          onMouseMove={bumpControlsVisibility}
          onMouseLeave={() => {
            if (!isPlaying) {
              setShowControls(true);
              return;
            }
            clearHideControlsTimer();
            hideControlsTimerRef.current = setTimeout(() => {
              setShowControls(false);
              hideControlsTimerRef.current = null;
            }, CONTROLS_HIDE_DELAY_MS);
          }}
          onTouchStart={bumpControlsVisibility}
        >
          <video
            ref={videoRef}
            key={video?.id}
            className="w-full h-full"
            onTimeUpdate={handleTimeUpdate}
            onLoadedMetadata={handleLoadedMetadata}
            onLoadedData={syncDurationFromVideo}
            onDurationChange={syncDurationFromVideo}
            onPlay={handlePlay}
            onPause={handlePause}
            onEnded={handleVideoEnd}
            onError={() => setPlaybackError('Failed to load video media')}
            onClick={() => {
              bumpControlsVisibility();
              togglePlay();
            }}
            playsInline
            preload="metadata"
          >
            {playbackType !== 'hls' &&
              playbackSources?.map((s) => (
                <source key={s.type} src={s.url} type={s.type} />
              ))}
          </video>

          {playbackError && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/70 px-6 text-center">
              <p className="text-sm text-zinc-200">{playbackError}</p>
            </div>
          )}

          {/* Controls Overlay */}
          <AnimatePresence>
            {showControls && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/40 text-white"
              >
                {/* Top Bar */}
                <div className="absolute top-0 left-0 right-0 p-3 sm:p-4 flex items-center gap-2">
                  <Link to={returnTo} state={returnState} className="shrink-0">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-10 px-2 text-white hover:bg-white/20 sm:h-9 sm:px-3"
                    >
                      <ChevronLeft className="w-5 h-5 sm:mr-1" />
                      <span className="hidden sm:inline">{t("previous")}</span>
                    </Button>
                  </Link>

                  <h1 className="min-w-0 flex-1 text-sm sm:text-lg font-medium truncate text-white drop-shadow-sm text-center sm:text-left">
                    {video.title}
                  </h1>

                  <div className="w-10 shrink-0 sm:w-24" />
                </div>

                {/* Center Play Button */}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      bumpControlsVisibility();
                      togglePlay();
                    }}
                    className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center pointer-events-auto"
                  >
                    {isPlaying ? (
                      <Pause className="w-7 h-7 sm:w-8 sm:h-8 text-white" />
                    ) : (
                      <Play className="w-7 h-7 sm:w-8 sm:h-8 text-white fill-white ml-1" />
                    )}
                  </motion.button>
                </div>

                {/* Bottom Controls */}
                <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-4 space-y-2 sm:space-y-3">
                  <Slider
                    value={[currentTime]}
                    max={duration > 0 ? duration : 100}
                    step={0.05}
                    min={0}
                    onValueChange={handleSeek}
                    className="w-full [&_[role=slider]]:h-4 [&_[role=slider]]:w-4 [&_[role=slider]]:border-white/60 [&_[role=slider]]:bg-white sm:[&_[role=slider]]:h-3.5 sm:[&_[role=slider]]:w-3.5 [&_.bg-primary]:bg-white [&_.bg-primary\\/20]:bg-white/25"
                  />

                  <div className="flex items-center justify-between text-[11px] tabular-nums text-white/80 sm:hidden">
                    <span>{formatTime(currentTime)}</span>
                    <span>{formatTime(duration)}</span>
                  </div>

                  <div className="flex items-center justify-between gap-1">
                    <div className="flex items-center gap-0.5 sm:gap-2 min-w-0">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          bumpControlsVisibility();
                          togglePlay();
                        }}
                        className="h-11 w-11 shrink-0 text-white hover:bg-white/20 sm:h-10 sm:w-10"
                      >
                        {isPlaying ? (
                          <Pause className="w-5 h-5" />
                        ) : (
                          <Play className="w-5 h-5" />
                        )}
                      </Button>

                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          bumpControlsVisibility();
                          seek(-10);
                        }}
                        className="h-11 w-11 shrink-0 text-white hover:bg-white/20 sm:h-10 sm:w-10"
                      >
                        <SkipBack className="w-5 h-5" />
                      </Button>

                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          bumpControlsVisibility();
                          seek(10);
                        }}
                        className="h-11 w-11 shrink-0 text-white hover:bg-white/20 sm:h-10 sm:w-10"
                      >
                        <SkipForward className="w-5 h-5" />
                      </Button>

                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={toggleMute}
                        className="h-11 w-11 shrink-0 text-white hover:bg-white/20 sm:h-10 sm:w-10"
                      >
                        {isMuted ? (
                          <VolumeX className="w-5 h-5" />
                        ) : (
                          <Volume2 className="w-5 h-5" />
                        )}
                      </Button>
                      <Slider
                        value={[isMuted ? 0 : volume]}
                        max={1}
                        step={0.1}
                        onValueChange={handleVolumeChange}
                        className="hidden w-20 md:flex [&>span]:bg-white/30 [&>span>span]:bg-white [&_[role=slider]]:border-white/60 [&_[role=slider]]:bg-white"
                      />

                      <span className="hidden sm:inline text-sm text-white/80 ml-1 tabular-nums shrink-0">
                        {formatTime(currentTime)} / {formatTime(duration)}
                      </span>
                    </div>

                    <div className="flex items-center gap-0.5 sm:gap-2 shrink-0">
                      <div className="hidden items-center gap-2 mr-2 lg:flex">
                        <span className="text-xs text-white/60">
                          {t("autoPlay")}
                        </span>
                        <Switch
                          checked={autoPlay}
                          onCheckedChange={setAutoPlay}
                          className="data-[state=checked]:bg-indigo-500"
                        />
                      </div>

                      {prevVideo && (
                        <Link to={buildVideoTarget(prevVideo, returnTo)} state={returnState} className="hidden sm:block">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-10 w-10 text-white hover:bg-white/20"
                          >
                            <ChevronLeft className="w-5 h-5" />
                          </Button>
                        </Link>
                      )}
                      {nextVideo && (
                        <Link to={buildVideoTarget(nextVideo, returnTo)} state={returnState} className="hidden sm:block">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-10 w-10 text-white hover:bg-white/20"
                          >
                            <ChevronRight className="w-5 h-5" />
                          </Button>
                        </Link>
                      )}

                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="hidden h-10 w-10 text-white hover:bg-white/20 md:inline-flex"
                          >
                            <Subtitles className="w-5 h-5" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                          side="top"
                          align="end"
                          className="bg-zinc-900 border-zinc-700 text-white"
                        >
                          <DropdownMenuLabel className="text-white/90">{t("subtitles")}</DropdownMenuLabel>
                          <DropdownMenuSeparator className="bg-zinc-700" />
                          {subtitleLanguages.map((lang) => (
                            <DropdownMenuItem
                              key={lang.code}
                              onClick={() => setSubtitle(lang.code)}
                              className="flex items-center justify-between text-white focus:bg-white/15 focus:text-white data-[highlighted]:bg-white/15 data-[highlighted]:text-white"
                            >
                              {lang.label}
                              {subtitle === lang.code && (
                                <Check className="w-4 h-4 text-indigo-400" />
                              )}
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>

                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-11 w-11 text-white hover:bg-white/20 sm:h-10 sm:w-10"
                          >
                            <Settings className="w-5 h-5" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                          side="top"
                          align="end"
                          className="bg-zinc-900 border-zinc-700 text-white"
                        >
                          <DropdownMenuLabel className="text-white/90">
                            {t("playbackSpeed")}
                          </DropdownMenuLabel>
                          <DropdownMenuSeparator className="bg-zinc-700" />
                          {playbackSpeeds.map((speed) => (
                            <DropdownMenuItem
                              key={speed}
                              onClick={() => changePlaybackSpeed(speed)}
                              className="flex items-center justify-between text-white focus:bg-white/15 focus:text-white data-[highlighted]:bg-white/15 data-[highlighted]:text-white"
                            >
                              {speed}x
                              {playbackSpeed === speed && (
                                <Check className="w-4 h-4 text-indigo-400" />
                              )}
                            </DropdownMenuItem>
                          ))}
                          <DropdownMenuSeparator className="bg-zinc-700 lg:hidden" />
                          <DropdownMenuItem
                            onClick={() => setAutoPlay((v) => !v)}
                            className="flex items-center justify-between text-white focus:bg-white/15 focus:text-white data-[highlighted]:bg-white/15 data-[highlighted]:text-white lg:hidden"
                          >
                            {t("autoPlay")}
                            {autoPlay && <Check className="w-4 h-4 text-indigo-400" />}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>

                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={togglePiP}
                        className="hidden h-10 w-10 text-white hover:bg-white/20 md:inline-flex"
                      >
                        <PictureInPicture2 className="w-5 h-5" />
                      </Button>

                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={toggleFullscreen}
                        className="h-11 w-11 text-white hover:bg-white/20 sm:h-10 sm:w-10"
                        title={isFullscreen ? t("exitFullscreen") : t("fullscreen")}
                      >
                        {isFullscreen ? (
                          <Minimize className="w-5 h-5" />
                        ) : (
                          <Maximize className="w-5 h-5" />
                        )}
                      </Button>
                    </div>
                  </div>

                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Info */}
      <div className="max-w-5xl mx-auto px-4 md:px-8 pt-3 pb-0 md:pt-4">
        <div className="grid lg:grid-cols-3 gap-4 md:gap-5">
          <div className="lg:col-span-2">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <div className="flex flex-wrap items-center gap-2 mb-2">
                {videoTagSlugs.map((slug) => (
                  <Badge key={slug} variant="outline" className="border-[#d4c4b0] text-[#5d4529] bg-white/60">
                    {getDictionaryLabel(TAG_FILTER_DICTIONARY, slug, language, slug)}
                  </Badge>
                ))}
              </div>

              <h1 className="text-2xl md:text-3xl font-bold mb-2 text-[#2d1810]">{video.title}</h1>

              <div className="flex items-center gap-4 text-sm text-[#6b5540] mb-0">
                <span className="flex items-center gap-1">
                  <Clock className="w-4 h-4 text-[#8b6f47]" />
                  {video.duration_minutes} {t("minutes")}
                </span>
                <Button
                  type="button"
                  size="sm"
                  variant={isMarkedWatched ? "secondary" : "outline"}
                  onClick={handleMarkAsWatched}
                  disabled={isMarkingWatched || isMarkedWatched}
                  className="h-8 px-3 border-[#d4c4b0] text-[#5d4529] hover:bg-[#f5ede3]"
                >
                  {isMarkedWatched ? t("watchedDone") : t("markAsWatched")}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={handleShare}
                  className="h-8 px-3 border-[#d4c4b0] text-[#5d4529] hover:bg-[#f5ede3]"
                >
                  <Share2 className="w-4 h-4 mr-1" />
                  共有
                </Button>
              </div>

            </motion.div>
          </div>

          <div className="lg:col-span-1">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="p-4 bg-white rounded-2xl border border-[#d4c4b0] shadow-sm"
            >
              <h3 className="font-semibold mb-2 flex items-center gap-2 text-[#2d1810]">
                <FileText className="w-5 h-5 text-[#8b6f47]" />
                配布資料・しゅくだいシート
              </h3>

              {video.materials && video.materials.length > 0 ? (
                <div className="space-y-2">
                  {video.materials.map((material, index) => {
                    const filename = suggestedMaterialFilename(material);

                    return (
                      <a
                        key={index}
                        href={material.url}
                        download={filename}
                        onClick={(e) => handleMaterialClick(e, material)}
                        className="flex items-center gap-3 p-3 bg-[#f5ede3] rounded-lg hover:bg-[#efe5d8] transition-colors group border border-transparent hover:border-[#d4c4b0] cursor-pointer"
                      >
                        <FileText className="w-4 h-4 flex-shrink-0 text-[#8b6f47] group-hover:text-[#6b5540]" />
                        <span className="flex-1 text-sm truncate text-[#2d1810]">{filename}</span>
                        <Download className="w-4 h-4 flex-shrink-0 text-[#8b6f47] group-hover:text-[#6b5540]" />
                      </a>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-[#6b5540]">配布資料・しゅくだいシートはありません</p>
              )}

              <h3 className="font-semibold mt-6 mb-2 flex items-center gap-2 text-[#2d1810]">
                <ExternalLink className="w-5 h-5 text-[#8b6f47]" />
                アンケートURL
              </h3>

              {video.questionnaire_url ? (
                <a
                  href={video.questionnaire_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 p-3 bg-[#f5ede3] rounded-lg hover:bg-[#efe5d8] transition-colors group border border-transparent hover:border-[#d4c4b0] cursor-pointer"
                >
                  <span className="flex-1 text-sm truncate text-[#2d1810]">{video.questionnaire_url}</span>
                  <ExternalLink className="w-4 h-4 flex-shrink-0 text-[#8b6f47] group-hover:text-[#6b5540]" />
                </a>
              ) : (
                <p className="text-sm text-[#6b5540]">アンケートURLはありません</p>
              )}
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}
